import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import qs from 'qs';
import {
  BuildingType,
  Contract,
  MainDiameter,
  PublicWaterSystem,
  ServiceLineMaterialMethod,
  ServiceLineType,
  State,
} from 'types/models';
import DataTableRecord from 'types/models/DataTableRecord';
import {
  Assumptions,
  AssumptionsOutput,
  Contractor,
  InventoryStatus,
  LeadStatusType,
  LifecycleStatus,
  Predictions,
  Private_Material,
  Verifications,
  VerificationsPerformed,
  VerificationTypes,
  VerifiedMaterials,
} from 'types/models/inventory';
import { PropertyOwnershipStatus } from 'types/models/PropertyOwnershipStatus';
import { PaginatedResponse } from 'types/PaginatedResponse';
import { BulkUpdateRequest } from 'types/requests/inventory/BulkUpdateRequest';
import { SubmitROEFormRequest } from 'types/requests/SubmitROEFormRequest';
import { ROEResponse } from 'types/responses/ROEResponse';

export class InventoryService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async getBuildingTypes(): Promise<BuildingType[]> {
    const { data } = await this.httpClient.get<BuildingType[]>(`/Dropdowns/BuildingTypes`, {
      withCredentials: false,
    });
    return data;
  }

  async getPropertyOwnershipStatuses(): Promise<PropertyOwnershipStatus[]> {
    const { data } = await this.httpClient.get<PropertyOwnershipStatus[]>(
      `/Dropdowns/PropertyOwnershipStatuses`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getServiceLineTypes(): Promise<ServiceLineType[]> {
    const { data } = await this.httpClient.get<ServiceLineType[]>(`/Dropdowns/ServiceLineTypes`, {
      withCredentials: false,
    });
    return data;
  }

  async getMainDiameters(): Promise<MainDiameter[]> {
    const { data } = await this.httpClient.get<MainDiameter[]>(`/Dropdowns/MainDiameters`, {
      withCredentials: false,
    });
    return data;
  }

  async getServiceLineMaterialMethods(): Promise<ServiceLineMaterialMethod[]> {
    const { data } = await this.httpClient.get<ServiceLineMaterialMethod[]>(
      `/Dropdowns/ServiceLineMaterialMethods`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getStates(): Promise<State[]> {
    const { data } = await this.httpClient.get<State[]>(`/Dropdowns/States`, {
      withCredentials: false,
    });
    return data;
  }

  async getZones(): Promise<string[]> {
    const { data } = await this.httpClient.get<string[]>(`/Inventory/Zones`, {
      withCredentials: false,
    });
    return data;
  }

  async getMaterials(): Promise<Private_Material[]> {
    const { data } = await this.httpClient.get<Private_Material[]>(`/Dropdowns/Materials`, {
      withCredentials: false,
    });
    return data;
  }

  async getPublicWaterSystems(): Promise<PublicWaterSystem[]> {
    const { data } = await this.httpClient.get<PublicWaterSystem[]>(
      `/Dropdowns/PublicWaterSystems`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getPwsIds(params = {}): Promise<PublicWaterSystem['PwsId'][]> {
    const { data } = await this.publicHttpClient.get<PublicWaterSystem['PwsId'][]>(
      `/properties/pwsIds`,
      {
        params,
        paramsSerializer: { indexes: null },
        withCredentials: false,
      }
    );
    return data;
  }

  async getDataTable(
    includeVerifications = true,
    showOnlyEnabledProperties = true
  ): Promise<PaginatedResponse<DataTableRecord>> {
    const queryString = qs.stringify({
      includeVerifications,
      showOnlyEnabledProperties,
    });
    return this.httpClient.get<DataTableRecord[]>(`/Inventory/DataTable?${queryString}`);
  }

  async getPaginatedDataTable(
    page,
    limit = 25,
    sort = [],
    filters = {},
    showOnlyEnabledProperties = true
  ): Promise<PaginatedResponse<DataTableRecord>> {
    const filter = qs.parse(qs.stringify(filters), { ignoreQueryPrefix: true });
    let body: any = {
      ...filter,
      showOnlyEnabledProperties: showOnlyEnabledProperties,
      page: {
        number: page,
        size: limit,
      },
    };

    if (sort.length) {
      body = {
        ...body,
        sort: sort.join(),
      };
    }

    const resp = await this.publicHttpClient.post<DataTableRecord[]>(
      `/properties/datatable/query`,
      body,
      {
        withCredentials: false,
      }
    );

    if (resp.status === 404) {
      return {
        data: [],
        pagination: {
          prev: null,
          next: null,
          last: null,
          count: 0,
          limit: null,
        },
      };
    }
    return resp;
  }

  async getPropertyIds(searchQuery: string): Promise<string[]> {
    const { data } = await this.httpClient.get<string[]>(`/Dropdowns/PropertyIds`, {
      params: { q: searchQuery },
    });
    return data;
  }

  async getPropertyNames(searchQuery: string): Promise<string[]> {
    const { data } = await this.httpClient.get<string[]>(`/Dropdowns/PropertyNames`, {
      params: { q: searchQuery },
    });
    return data;
  }

  async getCensusTracts(searchQuery: string): Promise<string[]> {
    const { data } = await this.httpClient.get<string[]>(`/Dropdowns/Properties/CensusTracts`, {
      params: { q: searchQuery },
    });
    return data;
  }

  async getBlockGroups(searchQuery: string): Promise<string[]> {
    const { data } = await this.httpClient.get<string[]>(`/Dropdowns/Properties/BlockGroups`, {
      params: { q: searchQuery },
    });
    return data;
  }

  async getPropertyCities(searchQuery: string): Promise<string[]> {
    const { data } = await this.httpClient.get<string[]>(`/Dropdowns/PropertyCities`, {
      params: { q: searchQuery },
    });
    return data;
  }

  async getInventoryStatus(filters = {}): Promise<InventoryStatus[]> {
    const { data } = await this.publicHttpClient.get<InventoryStatus[]>(
      `/properties/kpi/inventoryStatus`,
      {
        params: {
          ...filters,
        },
        withCredentials: false,
      }
    );
    return data;
  }

  async getVerifications(filters = {}): Promise<Verifications> {
    const { data } = await this.publicHttpClient.get<Verifications>(
      `/properties/kpi/verificationsRecommended`,
      {
        params: {
          ...filters,
        },
        withCredentials: false,
      }
    );
    return data;
  }

  async getLeadAssumptions(filters = {}): Promise<Assumptions> {
    const { data } = await this.publicHttpClient.get<Assumptions>(`/properties/kpi/assumptions`, {
      params: {
        ...filters,
      },
      withCredentials: false,
    });
    return data;
  }

  async getAssumptionsOutput(): Promise<AssumptionsOutput[]> {
    const { data } = await this.httpClient.get<AssumptionsOutput[]>(`/Assumptions/Output`);
    return data;
  }

  async getVerificationsPerformed(filters: {}): Promise<VerificationsPerformed[]> {
    const { data } = await this.publicHttpClient.get<VerificationsPerformed[]>(
      `/properties/kpi/verificationsPerformed`,
      {
        params: {
          ...filters,
        },
        withCredentials: false,
      }
    );
    return data;
  }

  async getVerifiedMaterials(filters: {}): Promise<VerifiedMaterials[]> {
    const { data } = await this.publicHttpClient.get<VerifiedMaterials[]>(
      `/properties/kpi/verifiedMaterials`,
      {
        params: {
          ...filters,
        },
        withCredentials: false,
      }
    );
    return data;
  }

  async getVerificationType(filters: {}): Promise<VerificationTypes[]> {
    const { data } = await this.publicHttpClient.get<VerificationTypes[]>(
      `/properties/kpi/verificationType`,
      {
        params: {
          ...filters,
        },
        withCredentials: false,
      }
    );
    return data;
  }

  async getPredictionsML(filters: {}): Promise<Predictions> {
    const { data } = await this.publicHttpClient.get<Predictions>(`/properties/kpi/predictions`, {
      params: {
        ...filters,
      },
      withCredentials: false,
    });
    return data;
  }

  async getLeadStatusTypes(): Promise<LeadStatusType[]> {
    const { data } = await this.httpClient.get<LeadStatusType[]>(`/Dropdowns/LeadStatusTypes`);
    return data;
  }

  async getInventoryContracts(): Promise<Contract[]> {
    const { data } = await this.httpClient.get<Contract[]>(`/Inventory/Contracts`);
    return data;
  }

  async getInventoryContractors(): Promise<Contractor[]> {
    const { data } = await this.httpClient.get<Contractor[]>(`/Inventory/Contractors`);
    return data;
  }

  async getLifecycleStatuses(): Promise<LifecycleStatus[]> {
    const { data } = await this.httpClient.get<LifecycleStatus[]>(`/Dropdowns/LifecycleStatuses`);
    return data;
  }

  async getPropertyAddresses(searchQuery: string): Promise<string[]> {
    const { data } = await this.httpClient.get<string[]>(`/Dropdowns/PropertyAddresses`, {
      params: { q: searchQuery },
    });
    return data;
  }

  async bulkUpdate(editedRecords: BulkUpdateRequest): Promise<string> {
    const { data } = await this.httpClient.post<string>(`/Inventory/BulkUpdate`, editedRecords);
    return data;
  }

  async getROEForm(): Promise<ROEResponse> {
    const { data } = await this.httpClient.get<ROEResponse>(`/Forms/RightOfEntry`, null);
    return data;
  }

  async submitROEForm(roeFormData: SubmitROEFormRequest): Promise<void> {
    await this.httpClient.post(`/Inventory/RightOfEntry`, roeFormData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
}
